import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { Rating } from '@abyss/web/ui/Rating';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Constants } from '../../../common/Constants';
import { ConstantsLagoon } from '../../../common/ConstantsLagoon';
import { notXlScreenOnly, tabletScreen } from '../../../common/ConstantsStyles';
import { StyledNullAttributeDiv } from '../../../frontends/ProviderSearch/routes/MapView/CompareDrawer/utility/compareDrawer.styled';
import { NULL_ATTRIBUTE_STATE } from '../../../frontends/ProviderSearch/routes/MapView/CompareDrawer/utility/compareDrawerConstants';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { returnSuppressFlag } from '../../../utils/featureSuppress';
import { shouldUseGehaV2Styling } from '../../../utils/gehaThemeUtils';
import {
  FlexRatingContainer,
  NoRatingText,
  ProviderCardContent,
  ProviderReviewDot,
  RatingText,
  RatingTextWrapper,
  RatingsText,
  UnavailableTextListView,
  UnderlineRatingText,
  VoiceOnlyText,
  VoiceOnlyTextSpan,
} from '../DataCard.style';
import {
  groupedPinRatingStyles,
  ratingFontSizeStyles,
} from './ProviderRating.styles';
import { StarRating } from './StarRating';

const ratingTextCss = (isGroupedLocationDrawer, enableMapViewHideOption) =>
  isGroupedLocationDrawer
    ? {
        '.abyss-text-root': {
          fontSize: '14px',
          fontWeight: '400',
        },
      }
    : enableMapViewHideOption
    ? {
        '.abyss-text-root': {
          fontSize: '16px',
          fontWeight: '$normal',
          '@screen < $md': {
            fontSize: '14px',
          },
        },
      }
    : {};

type Props = {
  healthGradeRating: string | undefined;
  providerId: string;
  sectionTypeDerived: string;
  popupContainer?: boolean;
  surveyCount?: number;
  'data-testid'?: string;
  size: number;
  hideNumber?: boolean;
  underline?: boolean;
  enableMapViewHideOption?: boolean;
  expandedProviderListView?: boolean;
  isGroupedLocationDrawer?: boolean;
  papatientRatingStarsRef?: React.RefObject<HTMLDivElement>;
};
export const ProviderRating = ({
  popupContainer,
  healthGradeRating,
  providerId,
  sectionTypeDerived,
  'data-testid': testId = '',
  surveyCount,
  size,
  hideNumber,
  underline,
  enableMapViewHideOption = false,
  expandedProviderListView,
  isGroupedLocationDrawer,
  papatientRatingStarsRef,
}: Props) => {
  const suppressRatings = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.PATIENT_REVIEWS
  );
  const [providerRatings] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.GET_PROVIDER_RATINGS,
  ]);
  const surveyRating = surveyCount ? ` (${surveyCount})` : '';
  const surveyRatingWithoutBraces = surveyCount ? ` ${surveyCount}` : '';
  const TabletScreen = useMediaQuery(tabletScreen);
  const notXlScreen = useMediaQuery(notXlScreenOnly);
  const isSmallScreen = useMediaQuery('(max-width: 400px)');
  const { t } = useTranslation();

  const isGEHAV2 = shouldUseGehaV2Styling();

  return (
    <div ref={papatientRatingStarsRef}>
      <ProviderCardContent
        cssProps={{ enableMapViewHideOption }}
        data-testid="provider-ratings"
        tabIndex={0}
      >
        {healthGradeRating && !suppressRatings && providerRatings ? (
          <RatingTextWrapper
            css={ratingTextCss(
              isGroupedLocationDrawer,
              enableMapViewHideOption
            )}
            data-auto-testid="rating-wrap"
            data-testid="rating-wrap"
          >
            {enableMapViewHideOption ? (
              <>
                <VoiceOnlyText>{`${healthGradeRating} stars ${surveyCount} reviews`}</VoiceOnlyText>
                <FlexRatingContainer alignItems="center" direction="row">
                  <Rating
                    css={{
                      ...(isGroupedLocationDrawer
                        ? groupedPinRatingStyles(notXlScreen)
                        : ratingFontSizeStyles(
                            expandedProviderListView,
                            TabletScreen,
                            isGEHAV2
                          )),
                      ...(isSmallScreen && {
                        'abyss-rating-star': {
                          width: '16px',
                          '.abyss-icon-symbol': {
                            fontSize: '16px !important',
                          },
                        },
                      }),
                    }}
                    hideRating
                    hideReviews
                    rating={Number(healthGradeRating) ?? 1}
                    reviewCount={surveyCount}
                    reviewDescription={`${surveyCount} ${t(
                      'DATA_CARD.REVIEWS'
                    )}`}
                    size="small"
                  />
                  <span>{`${healthGradeRating}`}</span>
                  <ProviderReviewDot>.</ProviderReviewDot>
                  <span>{`${surveyRatingWithoutBraces} ${t(
                    'DATA_CARD.REVIEWS'
                  )}`}</span>
                </FlexRatingContainer>
              </>
            ) : (
              <>
                <VoiceOnlyTextSpan>{`${healthGradeRating} stars ${surveyCount} reviews`}</VoiceOnlyTextSpan>
                <StarRating
                  healthGradeRating={healthGradeRating}
                  key={`healthgrade-rating-${healthGradeRating}`}
                  param={0}
                  providerId={providerId}
                  ratingIndex={0}
                  sectionTypeDerived={sectionTypeDerived}
                />
                {underline ? (
                  <UnderlineRatingText
                    aria-hidden="true"
                    className="text"
                    color="$primary1"
                    data-auto-testid={testId}
                    data-testid={testId}
                    size={size}
                  >
                    {`${healthGradeRating}${surveyRating}`}
                  </UnderlineRatingText>
                ) : (
                  <RatingText
                    aria-hidden="true"
                    className="text"
                    color={popupContainer ? '$interactive1' : '$primary1'}
                    data-auto-testid={testId}
                    data-testid={testId}
                    fontWeight={popupContainer ? '$bold' : ''}
                    size={size}
                  >
                    {`${healthGradeRating}${surveyRating}`}
                  </RatingText>
                )}
              </>
            )}
          </RatingTextWrapper>
        ) : (
          healthGradeRating && (
            <RatingTextWrapper
              data-auto-testid="rating-wrap"
              data-testid="rating-wrap"
            >
              {enableMapViewHideOption ? (
                <>
                  <FlexRatingContainer alignItems="center" direction="row">
                    <Rating
                      hideRating
                      hideReviews
                      rating={Number(healthGradeRating) ?? 1}
                      reviewCount={surveyCount}
                      reviewDescription={`${surveyCount} ${t(
                        'DATA_CARD.REVIEWS'
                      )}`}
                      size="small"
                    />
                    <span>{`${healthGradeRating}`}</span>
                    <ProviderReviewDot>.</ProviderReviewDot>
                    <span>{`${surveyRatingWithoutBraces} ${t(
                      'DATA_CARD.REVIEWS'
                    )}`}</span>
                  </FlexRatingContainer>
                  <VoiceOnlyText>{`${healthGradeRating} stars`}</VoiceOnlyText>
                </>
              ) : (
                <>
                  {Constants.RATE_LIMITING.map((param, ratingIndex) => (
                    <StarRating
                      healthGradeRating={healthGradeRating}
                      key={`healthgrade-rating-${healthGradeRating}`}
                      param={param}
                      providerId={providerId}
                      ratingIndex={ratingIndex}
                      sectionTypeDerived={sectionTypeDerived}
                    />
                  ))}
                  <VoiceOnlyText>{`${healthGradeRating} stars`}</VoiceOnlyText>

                  <RatingsText
                    aria-hidden="true"
                    className="text"
                    color="$primary1"
                    data-auto-testid={testId}
                    data-testid={testId}
                    size={size}
                  >
                    <>{healthGradeRating}</>
                  </RatingsText>
                </>
              )}
            </RatingTextWrapper>
          )
        )}
        {!healthGradeRating && !hideNumber ? (
          enableMapViewHideOption ? (
            <UnavailableTextListView
              cssProps={{
                expandedProviderListView,
                isGroupedLocationDrawer,
                TabletScreen,
                isGEHAV2,
              }}
              data-testid="provider-no-rating-list-view"
            >
              {t('DATA_CARD.RATINGS_UNAVAILABLE')}
            </UnavailableTextListView>
          ) : (
            <NoRatingText
              cssProps={{ isGEHAV2 }}
              data-testid="provider-no-rating"
            >
              {t('DATA_CARD.RATINGS_NOT_AVAILABLE')}
            </NoRatingText>
          )
        ) : (
          !healthGradeRating && (
            <StyledNullAttributeDiv>
              {t(NULL_ATTRIBUTE_STATE.HEALTH_GRADE_RATINGS)}
            </StyledNullAttributeDiv>
          )
        )}
      </ProviderCardContent>
    </div>
  );
};
